export type MatchStatus =
  | "idle"
  | "countdown"
  | "playing"
  | "paused"
  | "finished";

export type SoundEvent = "wall" | "paddle" | "goal";

export type ArenaCallbacks = {
  onReady(): void;
  onScore(left: number, right: number): void;
  onStatus(status: MatchStatus): void;
  onCountdown(value: number | null): void;
  onGameOver(winner: 1 | 2): void;
  onSound(event: SoundEvent): void;
};

export type ArenaController = {
  startMatch(targetScore: number): void;
  togglePause(): void;
  destroy(): void;
};

type Paddle = {
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  vx: number;
  vy: number;
  width: number;
  height: number;
  color: number;
  pointerId: number | null;
};

type Ball = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
};

type TrailPoint = { x: number; y: number; life: number };
type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  color: number;
};

const RED = 0xff5c7c;
const BLUE = 0x4f9dff;
const WHITE = 0xf7fbff;

export async function createArenaGame(
  container: HTMLDivElement,
  callbacks: ArenaCallbacks,
): Promise<ArenaController> {
  const PhaserModule = await import("phaser");
  const Phaser = PhaserModule.default;
  type Graphics = import("phaser").GameObjects.Graphics;
  type Pointer = import("phaser").Input.Pointer;
  type Key = import("phaser").Input.Keyboard.Key;

  let sceneInstance: ArenaScene | null = null;

  class ArenaScene extends Phaser.Scene {
    private fieldGraphics!: Graphics;
    private actionGraphics!: Graphics;
    private status: MatchStatus = "idle";
    private statusBeforePause: MatchStatus = "playing";
    private score: [number, number] = [0, 0];
    private targetScore = 7;
    private countdownEndsAt = 0;
    private lastCountdown: number | null = null;
    private serveDirection = 0;
    private goalHeight = 220;
    private wallMargin = 24;
    private lastImpactAt = 0;
    private trailTimer = 0;
    private trail: TrailPoint[] = [];
    private particles: Particle[] = [];
    private keys!: Record<string, Key>;
    private left: Paddle = {
      x: 0,
      y: 0,
      targetX: 0,
      targetY: 0,
      vx: 0,
      vy: 0,
      width: 34,
      height: 160,
      color: RED,
      pointerId: null,
    };
    private right: Paddle = {
      x: 0,
      y: 0,
      targetX: 0,
      targetY: 0,
      vx: 0,
      vy: 0,
      width: 34,
      height: 160,
      color: BLUE,
      pointerId: null,
    };
    private ball: Ball = { x: 0, y: 0, vx: 0, vy: 0, radius: 16 };

    constructor() {
      super("arena");
    }

    create() {
      setSceneInstance(this);
      this.fieldGraphics = this.add.graphics();
      this.actionGraphics = this.add.graphics();
      this.input.addPointer(2);
      this.input.on("pointerdown", this.handlePointerDown, this);
      this.input.on("pointermove", this.handlePointerMove, this);
      this.input.on("pointerup", this.handlePointerUp, this);
      this.input.on("pointerupoutside", this.handlePointerUp, this);
      this.scale.on("resize", this.handleResize, this);
      this.keys = (this.input.keyboard?.addKeys({
        W: Phaser.Input.Keyboard.KeyCodes.W,
        A: Phaser.Input.Keyboard.KeyCodes.A,
        S: Phaser.Input.Keyboard.KeyCodes.S,
        D: Phaser.Input.Keyboard.KeyCodes.D,
        UP: Phaser.Input.Keyboard.KeyCodes.UP,
        LEFT: Phaser.Input.Keyboard.KeyCodes.LEFT,
        DOWN: Phaser.Input.Keyboard.KeyCodes.DOWN,
        RIGHT: Phaser.Input.Keyboard.KeyCodes.RIGHT,
        SPACE: Phaser.Input.Keyboard.KeyCodes.SPACE,
      }) ?? {}) as Record<string, Key>;
      this.keys.SPACE?.on("down", () => this.togglePause());

      this.layoutArena();
      this.resetPositions();
      this.drawFrame();
      callbacks.onReady();
    }

    update(time: number, deltaMs: number) {
      const dt = Math.min(deltaMs, 34) / 1000;
      this.updatePaddles(dt);
      this.updateParticles(dt);

      if (this.status === "countdown") {
        const remaining = Math.max(0, this.countdownEndsAt - time);
        const value = Math.ceil(remaining / 1000);
        if (value !== this.lastCountdown) {
          this.lastCountdown = value;
          callbacks.onCountdown(value > 0 ? value : null);
        }
        if (remaining <= 0) {
          this.setStatus("playing");
          this.launchBall();
        }
      } else if (this.status === "playing") {
        this.updateBall(dt, time);
      } else if (this.status === "idle") {
        this.ball.x = this.scale.width / 2 + Math.sin(time / 900) * 12;
        this.ball.y = this.scale.height / 2 + Math.cos(time / 1100) * 8;
      }

      this.drawFrame();
    }

    startMatch(targetScore: number) {
      this.targetScore = Math.max(1, Math.min(99, Math.round(targetScore)));
      this.score = [0, 0];
      this.serveDirection = Math.random() < 0.5 ? -1 : 1;
      callbacks.onScore(0, 0);
      this.resetPositions();
      this.startCountdown(3);
    }

    togglePause() {
      if (this.status === "playing" || this.status === "countdown") {
        this.statusBeforePause = this.status;
        this.setStatus("paused");
        return;
      }
      if (this.status !== "paused") return;

      if (this.statusBeforePause === "countdown") {
        this.startCountdown(2);
      } else {
        this.setStatus("playing");
      }
    }

    private startCountdown(seconds: number) {
      this.ball.vx = 0;
      this.ball.vy = 0;
      this.countdownEndsAt = this.time.now + seconds * 1000;
      this.lastCountdown = null;
      this.setStatus("countdown");
    }

    private setStatus(nextStatus: MatchStatus) {
      this.status = nextStatus;
      if (nextStatus !== "countdown") callbacks.onCountdown(null);
      callbacks.onStatus(nextStatus);
    }

    private handleResize() {
      this.layoutArena();
      this.resetPositions(this.status === "idle");
    }

    private layoutArena() {
      const width = Math.max(320, this.scale.width);
      const height = Math.max(240, this.scale.height);
      const shortSide = Math.min(width, height);
      this.wallMargin = Math.max(16, Math.min(28, shortSide * 0.026));
      this.goalHeight = Math.max(120, Math.min(280, height * 0.31));
      this.ball.radius = Math.max(12, Math.min(19, shortSide * 0.021));
      const paddleHeight = Math.max(92, Math.min(174, height * 0.19));
      const paddleWidth = Math.max(26, Math.min(40, shortSide * 0.042));
      this.left.height = paddleHeight;
      this.right.height = paddleHeight;
      this.left.width = paddleWidth;
      this.right.width = paddleWidth;
      this.drawField();
    }

    private resetPositions(centerBall = true) {
      const width = this.scale.width;
      const height = this.scale.height;
      this.left.x = this.left.targetX = width * 0.19;
      this.left.y = this.left.targetY = height / 2;
      this.right.x = this.right.targetX = width * 0.81;
      this.right.y = this.right.targetY = height / 2;
      this.left.vx = this.left.vy = this.right.vx = this.right.vy = 0;
      this.left.pointerId = this.right.pointerId = null;
      if (centerBall) {
        this.ball.x = width / 2;
        this.ball.y = height / 2;
      }
      this.ball.vx = this.ball.vy = 0;
      this.trail = [];
    }

    private launchBall() {
      const baseSpeed = Math.max(
        340,
        Math.min(610, Math.min(this.scale.width, this.scale.height) * 0.54),
      );
      const direction = this.serveDirection || (Math.random() < 0.5 ? -1 : 1);
      const angle = (Math.random() - 0.5) * 0.72;
      this.ball.vx = Math.cos(angle) * baseSpeed * direction;
      this.ball.vy = Math.sin(angle) * baseSpeed;
      this.serveDirection = 0;
    }

    private handlePointerDown(pointer: Pointer) {
      if (this.status !== "playing" && this.status !== "countdown") return;
      const paddle = pointer.x < this.scale.width / 2 ? this.left : this.right;
      if (paddle.pointerId !== null) return;
      paddle.pointerId = pointer.id;
      this.setPaddleTarget(paddle, pointer.x, pointer.y);
    }

    private handlePointerMove(pointer: Pointer) {
      const paddle =
        this.left.pointerId === pointer.id
          ? this.left
          : this.right.pointerId === pointer.id
            ? this.right
            : null;
      if (paddle) this.setPaddleTarget(paddle, pointer.x, pointer.y);
    }

    private handlePointerUp(pointer: Pointer) {
      if (this.left.pointerId === pointer.id) this.left.pointerId = null;
      if (this.right.pointerId === pointer.id) this.right.pointerId = null;
    }

    private setPaddleTarget(paddle: Paddle, x: number, y: number) {
      const isLeft = paddle === this.left;
      const centerGap = Math.max(42, this.scale.width * 0.035);
      const minX = isLeft
        ? this.wallMargin + paddle.width / 2
        : this.scale.width / 2 + centerGap + paddle.width / 2;
      const maxX = isLeft
        ? this.scale.width / 2 - centerGap - paddle.width / 2
        : this.scale.width - this.wallMargin - paddle.width / 2;
      paddle.targetX = Phaser.Math.Clamp(x, minX, maxX);
      paddle.targetY = Phaser.Math.Clamp(
        y,
        this.wallMargin + paddle.height / 2,
        this.scale.height - this.wallMargin - paddle.height / 2,
      );
    }

    private updatePaddles(dt: number) {
      const keyboardSpeed = Math.max(420, this.scale.height * 0.9);
      if (this.status === "playing" || this.status === "countdown") {
        if (this.left.pointerId === null) {
          let dx = 0;
          let dy = 0;
          if (this.keys.A?.isDown) dx -= 1;
          if (this.keys.D?.isDown) dx += 1;
          if (this.keys.W?.isDown) dy -= 1;
          if (this.keys.S?.isDown) dy += 1;
          if (dx || dy) {
            this.setPaddleTarget(
              this.left,
              this.left.targetX + dx * keyboardSpeed * dt,
              this.left.targetY + dy * keyboardSpeed * dt,
            );
          }
        }
        if (this.right.pointerId === null) {
          let dx = 0;
          let dy = 0;
          if (this.keys.LEFT?.isDown) dx -= 1;
          if (this.keys.RIGHT?.isDown) dx += 1;
          if (this.keys.UP?.isDown) dy -= 1;
          if (this.keys.DOWN?.isDown) dy += 1;
          if (dx || dy) {
            this.setPaddleTarget(
              this.right,
              this.right.targetX + dx * keyboardSpeed * dt,
              this.right.targetY + dy * keyboardSpeed * dt,
            );
          }
        }
      }

      for (const paddle of [this.left, this.right]) {
        const oldX = paddle.x;
        const oldY = paddle.y;
        const follow = 1 - Math.exp(-28 * dt);
        paddle.x += (paddle.targetX - paddle.x) * follow;
        paddle.y += (paddle.targetY - paddle.y) * follow;
        paddle.vx = (paddle.x - oldX) / Math.max(dt, 0.001);
        paddle.vy = (paddle.y - oldY) / Math.max(dt, 0.001);
      }
    }

    private updateBall(dt: number, time: number) {
      const speed = Math.hypot(this.ball.vx, this.ball.vy);
      const steps = Math.max(1, Math.ceil((speed * dt) / (this.ball.radius * 0.45)));
      const stepDt = dt / steps;

      for (let step = 0; step < steps; step += 1) {
        this.ball.x += this.ball.vx * stepDt;
        this.ball.y += this.ball.vy * stepDt;
        if (this.resolveBounds(time)) return;
        this.resolvePaddle(this.left, time);
        this.resolvePaddle(this.right, time);
      }

      this.trailTimer += dt;
      if (this.trailTimer >= 0.025) {
        this.trailTimer = 0;
        this.trail.unshift({ x: this.ball.x, y: this.ball.y, life: 1 });
        this.trail = this.trail.slice(0, 11);
      }
      for (const point of this.trail) point.life -= dt * 3.2;
      this.trail = this.trail.filter((point) => point.life > 0);
    }

    private resolveBounds(time: number) {
      const { radius } = this.ball;
      const height = this.scale.height;
      const width = this.scale.width;
      const goalTop = height / 2 - this.goalHeight / 2;
      const goalBottom = height / 2 + this.goalHeight / 2;

      if (this.ball.y - radius <= this.wallMargin) {
        this.ball.y = this.wallMargin + radius;
        this.ball.vy = Math.abs(this.ball.vy);
        this.emitImpact("wall", this.ball.x, this.ball.y, WHITE, time, 4);
      } else if (this.ball.y + radius >= height - this.wallMargin) {
        this.ball.y = height - this.wallMargin - radius;
        this.ball.vy = -Math.abs(this.ball.vy);
        this.emitImpact("wall", this.ball.x, this.ball.y, WHITE, time, 4);
      }

      const insideGoal = this.ball.y > goalTop && this.ball.y < goalBottom;
      if (!insideGoal) {
        if (this.ball.x - radius <= this.wallMargin) {
          this.ball.x = this.wallMargin + radius;
          this.ball.vx = Math.abs(this.ball.vx);
          this.emitImpact("wall", this.ball.x, this.ball.y, RED, time, 5);
        } else if (this.ball.x + radius >= width - this.wallMargin) {
          this.ball.x = width - this.wallMargin - radius;
          this.ball.vx = -Math.abs(this.ball.vx);
          this.emitImpact("wall", this.ball.x, this.ball.y, BLUE, time, 5);
        }
      } else if (this.ball.x < -radius * 1.5) {
        this.scoreGoal(2);
        return true;
      } else if (this.ball.x > width + radius * 1.5) {
        this.scoreGoal(1);
        return true;
      }

      return false;
    }

    private resolvePaddle(paddle: Paddle, time: number) {
      const halfWidth = paddle.width / 2;
      const halfHeight = paddle.height / 2;
      const closestX = Phaser.Math.Clamp(
        this.ball.x,
        paddle.x - halfWidth,
        paddle.x + halfWidth,
      );
      const closestY = Phaser.Math.Clamp(
        this.ball.y,
        paddle.y - halfHeight,
        paddle.y + halfHeight,
      );
      let dx = this.ball.x - closestX;
      let dy = this.ball.y - closestY;
      let distanceSquared = dx * dx + dy * dy;

      if (distanceSquared >= this.ball.radius * this.ball.radius) return;

      if (distanceSquared < 0.0001) {
        const horizontalDepth = halfWidth - Math.abs(this.ball.x - paddle.x);
        const verticalDepth = halfHeight - Math.abs(this.ball.y - paddle.y);
        if (horizontalDepth < verticalDepth) {
          dx = this.ball.x < paddle.x ? -1 : 1;
          dy = 0;
        } else {
          dx = 0;
          dy = this.ball.y < paddle.y ? -1 : 1;
        }
        distanceSquared = 1;
      }

      const distance = Math.sqrt(distanceSquared);
      const nx = dx / distance;
      const ny = dy / distance;
      const penetration = this.ball.radius - distance;
      this.ball.x += nx * (penetration + 0.8);
      this.ball.y += ny * (penetration + 0.8);

      const relativeVx = this.ball.vx - paddle.vx;
      const relativeVy = this.ball.vy - paddle.vy;
      const incoming = relativeVx * nx + relativeVy * ny;
      if (incoming < 0) {
        this.ball.vx -= 1.92 * incoming * nx;
        this.ball.vy -= 1.92 * incoming * ny;
        this.ball.vx += paddle.vx * 0.42;
        this.ball.vy += paddle.vy * 0.42;
        const currentSpeed = Math.hypot(this.ball.vx, this.ball.vy);
        const minSpeed = 330;
        const maxSpeed = Math.max(780, Math.min(this.scale.width, this.scale.height) * 1.35);
        if (currentSpeed > 0 && currentSpeed < minSpeed) {
          this.ball.vx = (this.ball.vx / currentSpeed) * minSpeed;
          this.ball.vy = (this.ball.vy / currentSpeed) * minSpeed;
        } else if (currentSpeed > maxSpeed) {
          this.ball.vx = (this.ball.vx / currentSpeed) * maxSpeed;
          this.ball.vy = (this.ball.vy / currentSpeed) * maxSpeed;
        }
        this.emitImpact(
          "paddle",
          this.ball.x - nx * this.ball.radius,
          this.ball.y - ny * this.ball.radius,
          paddle.color,
          time,
          12,
        );
      }
    }

    private scoreGoal(player: 1 | 2) {
      this.score[player - 1] += 1;
      callbacks.onScore(this.score[0], this.score[1]);
      callbacks.onSound("goal");
      navigator.vibrate?.(70);
      this.spawnGoalBurst(player === 1 ? BLUE : RED);

      if (this.score[player - 1] >= this.targetScore) {
        this.ball.vx = this.ball.vy = 0;
        this.setStatus("finished");
        callbacks.onGameOver(player);
        return;
      }

      this.serveDirection = player === 1 ? -1 : 1;
      this.ball.x = this.scale.width / 2;
      this.ball.y = this.scale.height / 2;
      this.startCountdown(2);
    }

    private emitImpact(
      sound: SoundEvent,
      x: number,
      y: number,
      color: number,
      time: number,
      amount: number,
    ) {
      if (time - this.lastImpactAt < 55) return;
      this.lastImpactAt = time;
      callbacks.onSound(sound);
      for (let index = 0; index < amount; index += 1) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 45 + Math.random() * 150;
        this.particles.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 0.28 + Math.random() * 0.3,
          color,
        });
      }
    }

    private spawnGoalBurst(color: number) {
      const x = color === BLUE ? this.scale.width - 34 : 34;
      for (let index = 0; index < 40; index += 1) {
        const angle = Math.random() * Math.PI * 2;
        const speed = 80 + Math.random() * 290;
        this.particles.push({
          x,
          y: this.scale.height / 2,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: 0.45 + Math.random() * 0.65,
          color,
        });
      }
    }

    private updateParticles(dt: number) {
      for (const particle of this.particles) {
        particle.x += particle.vx * dt;
        particle.y += particle.vy * dt;
        particle.vx *= 0.985;
        particle.vy *= 0.985;
        particle.life -= dt;
      }
      this.particles = this.particles.filter((particle) => particle.life > 0);
    }

    private drawField() {
      const g = this.fieldGraphics;
      const width = this.scale.width;
      const height = this.scale.height;
      const goalTop = height / 2 - this.goalHeight / 2;
      const goalBottom = height / 2 + this.goalHeight / 2;
      g.clear();

      g.fillStyle(0x07101f, 1);
      g.fillRect(0, 0, width, height);
      g.fillStyle(0x0a1930, 1);
      g.fillRoundedRect(
        this.wallMargin,
        this.wallMargin,
        width - this.wallMargin * 2,
        height - this.wallMargin * 2,
        26,
      );

      g.fillStyle(RED, 0.045);
      g.fillRect(this.wallMargin, this.wallMargin, width / 2 - this.wallMargin, height - this.wallMargin * 2);
      g.fillStyle(BLUE, 0.045);
      g.fillRect(width / 2, this.wallMargin, width / 2 - this.wallMargin, height - this.wallMargin * 2);

      g.lineStyle(2, 0xc9dcff, 0.11);
      g.lineBetween(width / 2, this.wallMargin + 22, width / 2, height - this.wallMargin - 22);
      g.strokeCircle(width / 2, height / 2, Math.min(92, height * 0.12));
      g.fillStyle(0xc9dcff, 0.16);
      g.fillCircle(width / 2, height / 2, 5);

      g.lineStyle(3, 0xb9ccee, 0.2);
      g.lineBetween(this.wallMargin, goalTop, this.wallMargin, this.wallMargin);
      g.lineBetween(
        this.wallMargin,
        this.wallMargin,
        width - this.wallMargin,
        this.wallMargin,
      );
      g.lineBetween(
        width - this.wallMargin,
        this.wallMargin,
        width - this.wallMargin,
        goalTop,
      );
      g.lineBetween(this.wallMargin, goalBottom, this.wallMargin, height - this.wallMargin);
      g.lineBetween(
        this.wallMargin,
        height - this.wallMargin,
        width - this.wallMargin,
        height - this.wallMargin,
      );
      g.lineBetween(
        width - this.wallMargin,
        height - this.wallMargin,
        width - this.wallMargin,
        goalBottom,
      );

      this.drawGoal(g, 0, goalTop, this.wallMargin + 12, this.goalHeight, RED, true);
      this.drawGoal(g, width - this.wallMargin - 12, goalTop, this.wallMargin + 12, this.goalHeight, BLUE, false);

      g.lineStyle(1, 0xdce8ff, 0.055);
      const gridStep = Math.max(46, Math.floor(height / 13));
      for (let y = this.wallMargin + gridStep; y < height - this.wallMargin; y += gridStep) {
        g.lineBetween(this.wallMargin + 18, y, width - this.wallMargin - 18, y);
      }
    }

    private drawGoal(
      g: Graphics,
      x: number,
      y: number,
      width: number,
      height: number,
      color: number,
      opensLeft: boolean,
    ) {
      g.fillStyle(color, 0.13);
      g.fillRect(x, y, width, height);
      g.lineStyle(4, color, 0.58);
      const edgeX = opensLeft ? x + width : x;
      g.lineBetween(edgeX, y, edgeX, y + height);
      g.lineStyle(1, color, 0.16);
      for (let offset = 14; offset < height; offset += 18) {
        g.lineBetween(x, y + offset, x + width, y + offset);
      }
    }

    private drawFrame() {
      const g = this.actionGraphics;
      g.clear();

      for (const particle of this.particles) {
        const alpha = Math.min(0.9, particle.life * 1.8);
        g.fillStyle(particle.color, alpha);
        g.fillCircle(particle.x, particle.y, 2 + particle.life * 4);
      }

      this.drawPaddle(g, this.left);
      this.drawPaddle(g, this.right);

      for (let index = this.trail.length - 1; index >= 0; index -= 1) {
        const point = this.trail[index];
        const ratio = point.life * (1 - index / (this.trail.length + 1));
        g.fillStyle(0xbdeaff, Math.max(0, ratio * 0.18));
        g.fillCircle(point.x, point.y, this.ball.radius * Math.max(0.3, ratio));
      }

      g.fillStyle(0x66d9ff, 0.12);
      g.fillCircle(this.ball.x, this.ball.y, this.ball.radius * 2.25);
      g.fillStyle(0xa9eaff, 0.3);
      g.fillCircle(this.ball.x, this.ball.y, this.ball.radius * 1.45);
      g.fillStyle(WHITE, 1);
      g.fillCircle(this.ball.x, this.ball.y, this.ball.radius);
      g.fillStyle(0xffffff, 0.8);
      g.fillCircle(
        this.ball.x - this.ball.radius * 0.28,
        this.ball.y - this.ball.radius * 0.32,
        this.ball.radius * 0.3,
      );
    }

    private drawPaddle(g: Graphics, paddle: Paddle) {
      const x = paddle.x - paddle.width / 2;
      const y = paddle.y - paddle.height / 2;
      const radius = paddle.width / 2;
      g.fillStyle(paddle.color, 0.12);
      g.fillRoundedRect(x - 10, y - 10, paddle.width + 20, paddle.height + 20, radius + 10);
      g.fillStyle(0x020812, 0.48);
      g.fillRoundedRect(x + 5, y + 8, paddle.width, paddle.height, radius);
      g.fillStyle(paddle.color, 1);
      g.fillRoundedRect(x, y, paddle.width, paddle.height, radius);
      g.fillStyle(0xffffff, 0.35);
      g.fillRoundedRect(x + paddle.width * 0.18, y + 8, paddle.width * 0.18, paddle.height - 16, radius);
    }
  }

  function setSceneInstance(scene: ArenaScene) {
    sceneInstance = scene;
  }

  const game = new Phaser.Game({
    type: Phaser.CANVAS,
    parent: container,
    backgroundColor: "#07101f",
    width: Math.max(320, container.clientWidth),
    height: Math.max(240, container.clientHeight),
    antialias: true,
    render: { roundPixels: false },
    fps: { target: 60, forceSetTimeOut: false },
    input: { activePointers: 3, touch: { capture: true } },
    scale: {
      mode: Phaser.Scale.RESIZE,
      autoCenter: Phaser.Scale.CENTER_BOTH,
    },
    scene: ArenaScene,
  });

  return {
    startMatch(targetScore: number) {
      sceneInstance?.startMatch(targetScore);
    },
    togglePause() {
      sceneInstance?.togglePause();
    },
    destroy() {
      sceneInstance = null;
      game.destroy(true);
    },
  };
}
